import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, SECTION_HINT, SECTION_TITLE, SUBSECTION_TITLE } from '../../components/portal/adminStyles';
import { CallSheetLinks } from '../../components/portal/CallSheetLinks';
import { SectionSwitch } from '../../components/portal/CallSheetSectionSwitches';
import { KitSection, ShotsSection } from '../../components/portal/CallSheetChecklists';
import { CallSheetCrew } from '../../components/portal/CallSheetCrew';
import { CallSheetPeople } from '../../components/portal/CallSheetPeople';
import { CallSheetLocations } from '../../components/portal/CallSheetLocations';
import { CallSheetSchedule, type ScheduleRow } from '../../components/portal/CallSheetSchedule';
import { CallSheetDelete } from '../../components/portal/CallSheetDelete';
import { CallSheetDetails } from '../../components/portal/CallSheetDetails';
import { downloadCallSheetPdf } from '../../utils/callSheetPdf';
import { fetchKnownContacts, type KnownContact } from '../../utils/knownContacts';
import { ALL_SECTIONS_ON, type CallSheetSectionKey, type CallSheetSections } from '../../utils/callSheetSections';
import { emptyLocation, firstMapUrl, flattenAddress, locationNames, readLocations, saveCallSheetLocations, type CallSheetLocation } from '../../utils/callSheetLocations';
import { callSheetDateLabel, fetchCallSheet, fetchStaffOptions, saveCallSheetCrew, type CallSheetCrewMember, type CallSheetPayload, type StaffOption } from '../../utils/callSheets';
import { saveShootDayFields, saveShootDaySchedule, saveShootDaySections, saveShootDayShots, saveShootDayKit, saveShootDayContacts, setStaffPhone, setClientContactPhone } from '../../utils/shootDaySource';
import type { ShootDayContact, ShootDayContactKind, ShootDayFields, ShootDayKitItem, ShootDayScheduleItem, ShootDayShotItem, KitSectionName, ShotSectionName } from '../../utils/shootDaySource';
const EMPTY_FIELDS: ShootDayFields = {
  weather: '',
  parkingNotes: '',
  locationAddress: '',
  locationMapUrl: '',
  importantInstructions: '',
  scriptUrl: '',
  guestBiosUrl: '',
  storyboardUrl: '',
  dropboxUrl: ''
};
type KnownByKind = Record<ShootDayContactKind, KnownContact[]>;
const NO_SUGGESTIONS: KnownByKind = {
  client: [],
  guest: [],
  crew: []
};

/**
 * A key for a schedule row that survives being typed into and dragged
 * around (27 Oct 2026). React needs a stable key per row and framer-motion
 * needs a stable identity to animate a reorder; the array index is neither,
 * because moving a row changes it. The database id cannot do the job either
 * — a row added in this session has no id until the sheet is saved.
 *
 * It never leaves the browser: `saveShootDaySchedule` maps named fields.
 */
let uidCounter = 0;
function newUid(): string {
  uidCounter += 1;
  return `row-${Date.now().toString(36)}-${uidCounter}`;
}
function withUid(item: ShootDayScheduleItem): ScheduleRow {
  return {
    ...item,
    uid: item.id ?? newUid()
  };
}
function emptyScheduleItem(): ScheduleRow {
  return {
    uid: newUid(),
    timeLabel: '',
    endTimeLabel: '',
    title: '',
    durationNote: '',
    peopleNote: '',
    locationNote: '',
    note: ''
  };
}
function emptyShot(section: ShotSectionName): ShootDayShotItem {
  return {
    section,
    name: '',
    cameraLabel: '',
    referenceImageUrl: ''
  };
}
function emptyKitItem(section: KitSectionName): ShootDayKitItem {
  return {
    section,
    name: '',
    quantity: '',
    owner: ''
  };
}
function emptyContact(): ShootDayContact {
  return {
    role: '',
    name: '',
    phone: '',
    email: '',
    remote: false
  };
}

/**
 * One call sheet, top to bottom.
 *
 * Addressed by call sheet rather than by project since 13 Sep 2026 — a
 * project can now have several, and a call sheet can have no project at all.
 * Everything on this page belongs to the one day.
 *
 * **Important Instructions removed 28 Oct 2026, Tom's call** — *"the important
 * info will be in this sheet anyway"*. It had been used once in four sheets,
 * for "Please be on time" and "keep walkways clear", which is standing studio
 * practice rather than anything about a particular day. The stored text is
 * deliberately left in the database and still saved back untouched: the field
 * is gone from the page, not from the record.
 *
 * **Locations became a list the same day.** See `utils/callSheetLocations.ts`
 * for why, and for the mirror that keeps the separate crew app working.
 */
export function CallSheetBuilder({
  callSheetId,
  onBack
}: {
  callSheetId: string;
  onBack: () => void;
}) {
  const {
    notify
  } = usePortal();
  const [status, setStatus] = useState<'loading' | 'missing' | 'ready'>('loading');
  const [sheet, setSheet] = useState<CallSheetPayload | null>(null);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const [editingDetails, setEditingDetails] = useState(false);
  const [fields, setFields] = useState<ShootDayFields>(EMPTY_FIELDS);
  const [locations, setLocations] = useState<CallSheetLocation[]>([]);
  const [schedule, setSchedule] = useState<ScheduleRow[]>([]);
  const [shots, setShots] = useState<ShootDayShotItem[]>([]);
  const [kit, setKit] = useState<ShootDayKitItem[]>([]);
  const [crew, setCrew] = useState<CallSheetCrewMember[]>([]);
  const [clientPhone, setClientPhone] = useState('');
  const [clientContacts, setClientContacts] = useState<ShootDayContact[]>([]);
  const [guests, setGuests] = useState<ShootDayContact[]>([]);
  const [extraCrew, setExtraCrew] = useState<ShootDayContact[]>([]);
  const [sections, setSections] = useState<CallSheetSections>(ALL_SECTIONS_ON);
  const [savingSections, setSavingSections] = useState(false);

  /** Everyone typed onto a call sheet before, offered in the name fields. */
  const [known, setKnown] = useState<KnownByKind>(NO_SUGGESTIONS);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  async function load() {
    setStatus('loading');
    const [payload, options, knownCrew, knownClients, knownGuests] = await Promise.all([fetchCallSheet(callSheetId), fetchStaffOptions(), fetchKnownContacts('crew'), fetchKnownContacts('client'), fetchKnownContacts('guest')]);
    setStaffOptions(options);
    /* Reloaded after every save as well as on open, so somebody typed in on
       this sheet is offered on the next one without a refresh. */
    setKnown({
      crew: knownCrew,
      client: knownClients,
      guest: knownGuests
    });
    if (!payload) {
      setSheet(null);
      setStatus('missing');
      return;
    }
    setSheet(payload);
    setFields({
      weather: payload.weather ?? '',
      parkingNotes: payload.parkingNotes ?? '',
      locationAddress: payload.locationAddress ?? '',
      locationMapUrl: payload.locationMapUrl ?? '',
      /* Kept and saved back untouched. The field is off the page; the text is
         still in the record. */
      importantInstructions: payload.importantInstructions ?? '',
      scriptUrl: payload.scriptUrl ?? '',
      guestBiosUrl: payload.guestBiosUrl ?? '',
      storyboardUrl: payload.storyboardUrl ?? '',
      dropboxUrl: payload.dropboxUrl ?? ''
    });
    /* Falls back to splitting the old single address box, so a sheet written
       before today opens with its places already separated. */
    setLocations(readLocations(payload as unknown as Record<string, unknown>));
    setSchedule(payload.schedule.map(withUid));
    setShots(payload.shots);
    setKit(payload.kit);
    setCrew(payload.crew);
    setClientPhone(payload.clientContactPhone ?? '');
    setClientContacts(payload.clientContacts ?? []);
    setGuests(payload.guests ?? []);
    setExtraCrew(payload.extraCrew ?? []);
    setSections(payload.sections ?? ALL_SECTIONS_ON);
    setStatus('ready');
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSheetId]);

  /** Segment switches save on click, like crew phone numbers — a switch
      flipped on the way out of the door shouldn't be lost to an unsaved form.
      Optimistic, with a real revert if the database refuses. */
  async function toggleSection(key: CallSheetSectionKey) {
    if (!sheet || savingSections) return;
    const previous = sections;
    const next = {
      ...sections,
      [key]: !sections[key]
    };
    setSections(next);
    setSavingSections(true);
    const result = await saveShootDaySections(sheet.id, next);
    setSavingSections(false);
    if (!result.ok) {
      setSections(previous);
      notify(result.error ?? 'That switch could not be saved.');
    }
  }
  function editField<K extends keyof ShootDayFields>(key: K, value: ShootDayFields[K]) {
    setFields(current => ({
      ...current,
      [key]: value
    }));
  }

  /**
   * Renaming a location renames it everywhere.
   *
   * The schedule holds the location by NAME, so without this a rename would
   * silently orphan every segment that referenced it — the sheet would still
   * read correctly to a person while quietly pointing at a place that no
   * longer exists in the list. Both lists are in this one form, so keeping
   * them in step costs one pass.
   */
  function updateLocation(index: number, patch: Partial<CallSheetLocation>) {
    const before = locations[index];
    setLocations(current => current.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
    const oldName = before?.name.trim() ?? '';
    const newName = patch.name?.trim();
    if (newName === undefined || !oldName || newName === oldName) return;
    setSchedule(current => current.map(row => row.locationNote.trim() === oldName ? {
      ...row,
      locationNote: newName
    } : row));
  }
  function updateSchedule(index: number, patch: Partial<ShootDayScheduleItem>) {
    setSchedule(current => current.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
  }
  function updateShot(index: number, patch: Partial<ShootDayShotItem>) {
    setShots(current => current.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
  }
  function updateKitItem(index: number, patch: Partial<ShootDayKitItem>) {
    setKit(current => current.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
  }
  function updateContact(setter: React.Dispatch<React.SetStateAction<ShootDayContact[]>>, index: number, patch: Partial<ShootDayContact>) {
    setter(current => current.map((item, i) => i === index ? {
      ...item,
      ...patch
    } : item));
  }
  async function saveCrewPhone(userId: string, phone: string) {
    const result = await setStaffPhone(userId, phone);
    if (!result.ok) notify(result.error ?? 'Phone number could not be saved.');
  }
  async function saveClientPhone() {
    if (!sheet?.projectId) return;
    const result = await setClientContactPhone(sheet.projectId, clientPhone);
    if (!result.ok) notify(result.error ?? 'Client phone could not be saved.');
  }

  /* The flattened list, sent with the plain fields so the old single-address
     columns agree with `locations` whichever save lands first. The crew app
     and the PDF still read those columns. */
  const mirroredFields = (): ShootDayFields => ({
    ...fields,
    locationAddress: flattenAddress(locations),
    locationMapUrl: firstMapUrl(locations)
  });
  async function saveAll(event: React.FormEvent) {
    event.preventDefault();
    if (!sheet) return;
    setSaving(true);
    setSaveError('');

    /* Switched-off segments are still saved in full. The switch governs what
       gets published, never what gets kept. Crew inherited from a project is
       filtered out of the crew save — it belongs to the project's Team, and
       writing it here would duplicate one fact in two places.
        The schedule is sent in the order it appears on screen, which is what
       makes dragging a segment stick: set_shoot_day_schedule writes
       order_index from the array position. */
    const results = await Promise.all([saveShootDayFields(sheet.id, mirroredFields()), saveCallSheetLocations(sheet.id, locations), saveShootDaySchedule(sheet.id, schedule.filter(i => i.timeLabel.trim() || i.title.trim())), saveShootDayShots(sheet.id, shots.filter(i => i.name.trim())), saveShootDayKit(sheet.id, kit.filter(i => i.name.trim())), saveShootDaySections(sheet.id, sections), saveCallSheetCrew(sheet.id, crew.filter(m => !m.fromProject).map(m => ({
      userId: m.userId,
      role: m.role ?? ''
    }))), saveShootDayContacts(sheet.id, 'client', clientContacts.filter(i => i.name.trim())), saveShootDayContacts(sheet.id, 'guest', guests.filter(i => i.name.trim())), saveShootDayContacts(sheet.id, 'crew', extraCrew.filter(i => i.name.trim()))]);
    setSaving(false);
    const failed = results.find(result => !result.ok);
    if (failed) {
      setSaveError(failed.error ?? 'Something could not be saved.');
      return;
    }
    notify('Call sheet saved');
    await load();
  }
  async function handleDownloadPdf() {
    if (!sheet) return;
    setDownloadingPdf(true);
    try {
      await downloadCallSheetPdf({
        projectName: sheet.title?.trim() || sheet.projectName || sheet.clientName,
        projectClient: sheet.clientName,
        dateLine: callSheetDateLabel(sheet.shootDate),
        fields: mirroredFields(),
        schedule,
        crew: crew.map(m => ({
          userId: m.userId,
          name: m.name,
          role: m.role ?? '',
          phone: m.phone
        })),
        extraCrew,
        clientContact: sheet.clientContactName ?? '',
        clientPhone,
        clientContacts,
        guests,
        kit,
        sections
      });
    } catch (error) {
      notify('The PDF could not be generated. Please try again.');
      console.error('[call sheet pdf]', error);
    } finally {
      setDownloadingPdf(false);
    }
  }
  if (status === 'loading') {
    return <p className="label mt-12 text-paper/35" data-id="element-2275" data-mp="2q563a8yfod0p">Loading…</p>;
  }
  if (status === 'missing' || !sheet) {
    return <div data-id="element-2276" data-mp="35iqj2ieybp74">
        <button type="button" onClick={onBack} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-2277" data-mp="30qfnfrr8upkd">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2278" data-mp="2vp00fu5ia8zc" />
          Call Sheets
        </button>
        <p className="mt-10 font-display text-2xl text-paper/50" data-id="element-2279" data-mp="3of8a0xlz7l2b">That call sheet no longer exists.</p>
      </div>;
  }
  const heading = sheet.title?.trim() || sheet.projectName || sheet.clientName;
  const context = [sheet.clientName, sheet.showName, sheet.projectName].filter((part): part is string => Boolean(part && part.trim())).filter((part, index, all) => all.indexOf(part) === index).join('   ·   ');
  return <div data-id="element-2280" data-mp="23xk7lidcvq0o">
      <button type="button" onClick={onBack} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-2281" data-mp="1z13h4hioozkf">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2282" data-mp="jhvvzper4fh7" />
        Call Sheets
      </button>

      {/* The date leads, because a call sheet is a day before it is anything
          else, and with several per production the date is what tells two of
          them apart at a glance.
           Edit details sits up here with the identity it edits, and OUTSIDE
          the main form below — it is its own form, and a form inside a form
          is invalid markup that browsers resolve by dropping one of them. */}
      <header className="mt-8" data-id="element-2283" data-mp="1b9bou16wiyfv">
        <div className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3" data-id="element-2284" data-mp="1b8igivsao0vm">
          <span className="label text-paper/40" data-id="element-2285" data-mp="3904thdt01mlk">{callSheetDateLabel(sheet.shootDate)}</span>
          {!editingDetails && <button type="button" onClick={() => setEditingDetails(true)} className={BUTTON_QUIET} data-id="element-2286" data-mp="3d2hq9cprqa67">
              Edit Details
            </button>}
        </div>
        <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2287" data-mp="f8552yuthc5k">{heading}</h1>
        {context && <p className="label-sm mt-5 text-paper/40" data-id="element-2288" data-mp="t7xvsu6cfsdj">{context}</p>}

        {editingDetails && <CallSheetDetails sheet={sheet} onCancel={() => setEditingDetails(false)} onSaved={() => {
        setEditingDetails(false);
        notify('Call sheet details saved');
        void load();
      }} data-id="element-2289" data-mp="p3q4cp8i3ybm" />}
      </header>

      <form onSubmit={saveAll} data-id="element-2290" data-mp="1ctsvn34ld096">
        <section className="mt-12" data-id="element-2291" data-mp="17vrb0driosvl">
          <h2 className={SECTION_TITLE} data-id="element-2292" data-mp="17wtzyg5a4l8v">Links</h2>
          <CallSheetLinks crewToken={sheet.crewToken} clientToken={sheet.clientToken} onDownloadPdf={handleDownloadPdf} downloadingPdf={downloadingPdf} data-id="element-2293" data-mp="1lva542nhu3ow" />
        </section>

        <CallSheetLocations locations={locations} onAdd={() => setLocations(c => [...c, emptyLocation()])} onUpdate={updateLocation} onRemove={index => setLocations(c => c.filter((_, i) => i !== index))} data-id="element-2294" data-mp="20hpn75arni17" />

        {/* Parking sits with the places it is about, rather than in Extras —
            it is the second thing a driver asks after the address. */}
        <div className="mt-8 max-w-md" data-id="element-2295" data-mp="1hqgc4rlckl7i">
          <label className="block" data-id="element-2296" data-mp="1hpeq5s2smeb0">
            <span className="label block text-paper/40" data-id="element-2297" data-mp="1miq0w4aq7f87">Parking Notes</span>
            <input value={fields.parkingNotes} onChange={event => editField('parkingNotes', event.target.value)} placeholder="e.g. Basement 2, use the service lift" className={FIELD} data-id="element-2298" data-mp="17tbp6pjoglpn" />
          </label>
        </div>

        <CallSheetCrew crew={crew} options={staffOptions} hasProject={Boolean(sheet.projectId)} projectName={sheet.projectName} onAdd={userId => {
        const option = staffOptions.find(o => o.userId === userId);
        if (!option) return;
        setCrew(current => [...current, {
          userId,
          name: option.name,
          role: '',
          phone: option.phone,
          fromProject: false
        }]);
      }} onRemove={userId => setCrew(current => current.filter(m => m.userId !== userId))} onRoleChange={(userId, role) => setCrew(current => current.map(m => m.userId === userId ? {
        ...m,
        role
      } : m))} onPhoneChange={(userId, phone) => setCrew(current => current.map(m => m.userId === userId ? {
        ...m,
        phone
      } : m))} onPhoneBlur={(userId, phone) => void saveCrewPhone(userId, phone)} data-id="element-2299" data-mp="yuq70vi4b8pe" />

        <section className="mt-14 border-t border-ruleDark pt-8" data-id="element-2300" data-mp="hjumqexwgt4g">
          <h2 className={SECTION_TITLE} data-id="element-2301" data-mp="cl1bkt635e7g">Client &amp; Guests</h2>
          <p className={SECTION_HINT} data-id="element-2302" data-mp="2o8mzts73nzju">
            {sheet.projectId ? 'The main client contact comes from the project. Anyone else goes below.' : 'No project attached, so every contact on this day is typed in here.'}
          </p>

          {sheet.projectId && <div className="mt-6 max-w-sm" data-id="element-2303" data-mp="2x7i777lvrfoq">
              <label className="block" data-id="element-2304" data-mp="327cuz2aczp5k">
                <span className="label block text-paper/40" data-id="element-2305" data-mp="326bictdp2ulq">
                  Client Contact{sheet.clientContactName ? ` — ${sheet.clientContactName}` : ''}
                </span>
                <input value={clientPhone} onChange={event => setClientPhone(event.target.value)} onBlur={saveClientPhone} placeholder="Phone number" className={FIELD} data-id="element-2306" data-mp="29k9xmdhf5kgw" />
              </label>
            </div>}

          <CallSheetPeople title="Extra Crew" hint="Freelance or on-the-day crew without a Portal OS account. Start typing a name you have used before and the rest fills itself in." items={extraCrew} addLabel="Add Extra Crew" showRemote known={known.crew} onAdd={() => setExtraCrew(c => [...c, emptyContact()])} onUpdate={(index, patch) => updateContact(setExtraCrew, index, patch)} onRemove={index => setExtraCrew(c => c.filter((_, i) => i !== index))} data-id="element-2307" data-mp="1lsbkrp2bnh1n" />

          <CallSheetPeople title="Client Contacts" hint="Brand or agency contacts on this day." items={clientContacts} addLabel="Add Client Contact" known={known.client} onAdd={() => setClientContacts(c => [...c, emptyContact()])} onUpdate={(index, patch) => updateContact(setClientContacts, index, patch)} onRemove={index => setClientContacts(c => c.filter((_, i) => i !== index))} data-id="element-2308" data-mp="elmx8quhyxa7" />

          <CallSheetPeople title="Featured Guests" hint="On-camera guests — not client, not crew." items={guests} addLabel="Add Guest" known={known.guest} onAdd={() => setGuests(c => [...c, emptyContact()])} onUpdate={(index, patch) => updateContact(setGuests, index, patch)} onRemove={index => setGuests(c => c.filter((_, i) => i !== index))} data-id="element-2309" data-mp="qkolvy9mttp" />
        </section>

        <CallSheetSchedule items={schedule} locationNames={locationNames(locations)} onAdd={() => setSchedule(c => [...c, emptyScheduleItem()])} onUpdate={updateSchedule} onRemove={index => setSchedule(c => c.filter((_, i) => i !== index))} onReorder={setSchedule} data-id="element-2310" data-mp="36g7nql97z7zs" />

        <ShotsSection on={sections.shots} onToggle={() => void toggleSection('shots')} saving={savingSections} projectId={sheet.projectId ?? sheet.id} shots={shots} onAdd={section => setShots(c => [...c, emptyShot(section)])} onUpdate={updateShot} onRemove={index => setShots(c => c.filter((_, i) => i !== index))} data-id="element-2311" data-mp="2rop144luuf7g" />

        <KitSection on={sections.kit} onToggle={() => void toggleSection('kit')} saving={savingSections} kit={kit} onAdd={section => setKit(c => [...c, emptyKitItem(section)])} onUpdate={updateKitItem} onRemove={index => setKit(c => c.filter((_, i) => i !== index))} data-id="element-2312" data-mp="1z4bh4w1g6wwj" />

        <section className="mt-14 border-t border-ruleDark pt-8" data-id="element-2313" data-mp="2vtst1sr0xf6o">
          <details className="group" data-id="element-2314" data-mp="2i3pfdq67mtsu">
            <summary className={`${SECTION_TITLE} flex cursor-pointer list-none items-center gap-3 marker:hidden [&::-webkit-details-marker]:hidden`} data-id="element-2315" data-mp="2i2nu6svhcqxe">
              <ChevronDownIcon className="h-5 w-5 shrink-0 text-paper/40 transition-transform duration-150 ease-out group-open:rotate-180" strokeWidth={1.5} data-id="element-2316" data-mp="3arbiyyfftyfp" />
              Extras
            </summary>
            <p className={SECTION_HINT} data-id="element-2317" data-mp="3ovaf2a9sn2g0">Optional — fill in whatever’s relevant, skip the rest.</p>

            {/* Weather isn't part of the Files switch — it prints in the
                summary block at the top of the sheet, not in Files. */}
            <div className="mt-6 grid grid-cols-1 gap-7 sm:grid-cols-2" data-id="element-2318" data-mp="3tnbs3yescx73">
              <label className="block" data-id="element-2319" data-mp="35y9b8srz78dn">
                <span className="label block text-paper/40" data-id="element-2320" data-mp="35zanv1on42xh">Weather</span>
                <input value={fields.weather} onChange={event => editField('weather', event.target.value)} placeholder="e.g. Sunny, 28°C" className={FIELD} data-id="element-2321" data-mp="31wxiiedt2718" />
              </label>
            </div>

            <div className="mt-10" data-id="element-2322" data-mp="v67r55ovou34">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3" data-id="element-2323" data-mp="v553r7qrl9ry">
                <span className={`${SUBSECTION_TITLE} ${sections.files ? '' : 'text-paper/35'}`} data-id="element-2324" data-mp="q7ms39ld4gg9">Files</span>
                <SectionSwitch label="Files" on={sections.files} onToggle={() => void toggleSection('files')} saving={savingSections} data-id="element-2325" data-mp="2vw6yy4jyhrzc" />
              </div>
              <p className="label-sm mt-2 max-w-xl leading-relaxed text-paper/35" data-id="element-2326" data-mp="35sa13aet5qma">
                {sections.files ? 'Links the crew can open from their call sheet, and printed at the foot of the PDF.' : 'Left off this call sheet — still yours to edit here, and one tap from coming back.'}
              </p>
              <div className={`mt-6 grid grid-cols-1 gap-7 sm:grid-cols-2 ${sections.files ? '' : 'opacity-50'}`} data-id="element-2327" data-mp="3ojja7iko2ddc">
                <label className="block" data-id="element-2328" data-mp="3tjfg5ncxcevj">
                  <span className="label block text-paper/40" data-id="element-2329" data-mp="3tl8n73rg9142">Dropbox / Drive Link</span>
                  <input value={fields.dropboxUrl} onChange={event => editField('dropboxUrl', event.target.value)} placeholder="https://…" className={FIELD} data-id="element-2330" data-mp="1h6fah3efnok" />
                </label>
                <label className="block" data-id="element-2331" data-mp="1lwmf6lhm9yyf">
                  <span className="label block text-paper/40" data-id="element-2332" data-mp="1lzqmhrqdpatb">Script Link</span>
                  <input value={fields.scriptUrl} onChange={event => editField('scriptUrl', event.target.value)} placeholder="https://…" className={FIELD} data-id="element-2333" data-mp="1hw2ej7xu4d7j" />
                </label>
                <label className="block" data-id="element-2334" data-mp="25lwsxtxsy9tw">
                  <span className="label block text-paper/40" data-id="element-2335" data-mp="20uohkscwwee3">Guest Bios Link</span>
                  <input value={fields.guestBiosUrl} onChange={event => editField('guestBiosUrl', event.target.value)} placeholder="https://…" className={FIELD} data-id="element-2336" data-mp="20sk7ttsc0l5w" />
                </label>
                <label className="block" data-id="element-2337" data-mp="2oiy0nmiqqo1j">
                  <span className="label block text-paper/40" data-id="element-2338" data-mp="37fziwlfjnz80">Storyboard Link</span>
                  <input value={fields.storyboardUrl} onChange={event => editField('storyboardUrl', event.target.value)} placeholder="https://…" className={FIELD} data-id="element-2339" data-mp="3cfksrqw95rzi" />
                </label>
              </div>
            </div>
          </details>
        </section>

        {saveError && <p role="alert" className="mt-8 label-sm text-paper/70" data-id="element-2340" data-mp="2oscq7gq7kx16">
            {saveError}
          </p>}

        {/* Save and delete share a line, with real space between them and
            delete set quiet — the destructive control should be findable
            without being the thing your hand lands on. */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-8 border-t border-ruleDark pt-8" data-id="element-2341" data-mp="dy8amu36vxej">
          <button type="submit" disabled={saving} className={BUTTON_SOLID} data-id="element-2342" data-mp="hztreqiugvu0">
            {saving ? 'Saving…' : 'Save Call Sheet'}
          </button>

          <CallSheetDelete callSheetId={sheet.id} name={heading} counts={{
          schedule: schedule.filter(i => i.timeLabel.trim() || i.title.trim()).length,
          shots: shots.filter(i => i.name.trim()).length,
          kit: kit.filter(i => i.name.trim()).length,
          contacts: [...extraCrew, ...clientContacts, ...guests].filter(i => i.name.trim()).length
        }} onDeleted={onBack} onError={message => notify(message)} data-id="element-2343" data-mp="22fi4knx3kefl" />
        </div>
      </form>
    </div>;
}